import { Stack, useTheme } from '@mui/material';
import { Children, useEffect, useMemo, useRef } from 'react';
import * as React from 'react';

import { ChevronDownIcon } from '@/components/icons/ChevronDownIcon';
import { ChevronUpIcon } from '@/components/icons/ChevronUpIcon';
import { MenuPopper } from '@/components/poppers/MenuPopper/MenuPopper';
import { Tooltip, TooltipProps } from '@/components/poppers/Tooltip/Tooltip';
import { Slot } from '@/components/utils/slots';
import { useFormSideDrawerMenuProps } from '@/modules/common/SideDrawer/FormSideDrawer';

import { Button, ButtonProps } from './Button';
import { ButtonRoot } from './ButtonRoot';
import { ChevronWithOutline } from './HeaderWithPopover';
import { getColorsForVariant, iconSizeByButtonSize } from './styles';
import { ButtonVariant } from './types';

export type ButtonWithPopoverSize = 'xs' | 'sm' | 'md';

export type ButtonWithPopoverProps = {
  label: React.ReactNode;
  // not all sizes are supported
  size: ButtonWithPopoverSize;
  type?: React.HTMLProps<HTMLButtonElement>['type'];
  popperProps?: Partial<React.ComponentProps<typeof MenuPopper>>;
  isOpen?: boolean;
  popperVariant?: 'menuBelow' | 'menuAbove';
  hideChevron?: boolean;
  buttonSx?: React.ComponentProps<typeof ButtonRoot>['sx'];
  onOpen?: () => void;
  onClose?: () => void;
  onMouseLeave?: (isOpen: boolean) => void;
  onWidthChange?: (width: number) => void;
  slots?: {
    Chevron?: Slot<typeof ChevronWithOutline>;
  };
  highlightWhenOpen?: boolean;
  keepOpen?: boolean;
  tooltip?: string;
  tooltipProps?: Partial<TooltipProps>;
} & ButtonProps;

function DefaultChevron({
  iconSize,
  isOpen,
}: {
  iconSize: number;
  isOpen: boolean;
}) {
  const sizeProps = { size: iconSize };
  return isOpen ? (
    <ChevronUpIcon {...sizeProps} />
  ) : (
    <ChevronDownIcon {...sizeProps} />
  );
}

function useActiveBackgroundColorStyles(
  variant: ButtonVariant,
  isActive: boolean,
  highlightWhenOpen?: boolean
) {
  const theme = useTheme();
  const colors = getColorsForVariant(variant, theme);

  if (isActive && highlightWhenOpen) {
    return {
      bgcolor: colors.regular.backgroundColorOnHover,
      ...(colors.regular.textColorOnHover && {
        color: colors.regular.textColorOnHover,
      }),
    };
  }

  if (variant === 'secondary' && isActive) {
    return {
      borderColor: theme.palette.buttons.light,
    };
  }

  return {};
}

export function ButtonWithPopover(props: ButtonWithPopoverProps) {
  const {
    children,
    label,
    type,
    popperProps,
    isOpen,
    popperVariant,
    hideChevron,
    buttonSx,
    onClick,
    onOpen,
    onClose,
    onMouseLeave,
    onWidthChange,
    slots,
    highlightWhenOpen,
    keepOpen,
    tooltip,
    tooltipProps = {},
    ...sharedButtonProps
  } = props;
  const menuButtonRef = useRef<HTMLButtonElement>(null);
  // Inside a FormSideDrawer this renders the menu inline (disablePortal) so it
  // stacks within the drawer instead of behind it; undefined (no-op) elsewhere.
  // Mirrors FormAwareSelectInput.
  const formSideDrawerMenuProps = useFormSideDrawerMenuProps();
  const [menuIsOpen, setMenuIsOpen] = React.useState(false);
  // When `isOpen` is provided externally, use it as the source of truth so
  // the caller can coordinate which dropdown is open.
  const effectiveIsOpen = isOpen !== undefined ? isOpen : menuIsOpen;
  const iconSize = iconSizeByButtonSize[sharedButtonProps.size];

  useEffect(() => {
    if (isOpen === true) {
      setMenuIsOpen(true);
      onOpen?.();
    } else if (isOpen === false && menuIsOpen) {
      setMenuIsOpen(false);
      onClose?.();
    }
  }, [isOpen, onClose, onOpen, setMenuIsOpen, menuIsOpen]);

  function handleClick(e: React.MouseEvent<HTMLButtonElement>) {
    e.stopPropagation();
    // In controlled mode (isOpen prop provided), don't toggle internal state —
    // let the caller manage open/close via isOpen.
    if (isOpen === undefined) {
      const willOpen = !menuIsOpen;
      setMenuIsOpen(willOpen);
      if (willOpen) {
        onOpen?.();
      } else {
        onClose?.();
      }
    }
    onClick?.(e);
  }

  const positioning = useMemo(() => {
    if (popperVariant === 'menuBelow') {
      return {
        anchorOrigin: { horizontal: 'left', vertical: 'bottom' },
        transformOrigin: { horizontal: 'left', vertical: 0 },
      } as const;
    }

    return {
      anchorOrigin: { horizontal: 'right', vertical: 0 },
      transformOrigin: { horizontal: 'right', vertical: 'bottom' },
    } as const;
  }, [popperVariant]);

  const activeBackgroundColorStyles = useActiveBackgroundColorStyles(
    sharedButtonProps.variant,
    effectiveIsOpen,
    highlightWhenOpen
  );

  const ChevronIcon = hideChevron ? undefined : (
    <DefaultChevron iconSize={iconSize} isOpen={effectiveIsOpen} />
  );

  const buttonSlots = useMemo(() => {
    const EndIcon =
      slots?.Chevron && !hideChevron
        ? {
            EndIcon: {
              component: slots.Chevron.component,
              props: {
                ...slots.Chevron.props,
                iconSize,
                isOpen: effectiveIsOpen,
              },
            },
          }
        : {};

    return {
      ...EndIcon,
    };
  }, [hideChevron, iconSize, effectiveIsOpen, slots?.Chevron]);

  const buttonWidth = menuButtonRef.current?.offsetWidth;

  useEffect(() => {
    if (!onWidthChange || !buttonWidth) return;
    onWidthChange(buttonWidth);
  }, [buttonWidth, onWidthChange]);

  const numChildren = Children.count(children);
  const showPopper = numChildren > 0;

  return (
    <Stack>
      {showPopper && (
        <MenuPopper
          {...positioning}
          open={effectiveIsOpen}
          anchorRef={menuButtonRef}
          onClose={() => {
            setMenuIsOpen(false);
            onClose?.();
          }}
          onClick={() => {
            if (keepOpen) return;
            setMenuIsOpen(false);
            onClose?.();
          }}
          {...formSideDrawerMenuProps}
          {...popperProps}
        >
          {children}
        </MenuPopper>
      )}
      <Tooltip title={tooltip} show={!!tooltip} arrow {...tooltipProps}>
        <Button
          ref={menuButtonRef}
          sx={{
            gap: 1,
            ...activeBackgroundColorStyles,
            ...buttonSx,
          }}
          type={type}
          {...sharedButtonProps}
          onMouseLeave={() => {
            onMouseLeave?.(menuIsOpen);
          }}
          onClick={handleClick}
          endIcon={ChevronIcon ? () => ChevronIcon : undefined}
          slots={buttonSlots}
        >
          {label}
        </Button>
      </Tooltip>
    </Stack>
  );
}
